import { Stack, Typography, useTheme } from "@mui/material";
import { type ReactNode, useLayoutEffect, useRef, useState } from "react";

/** Gap between the arrow tip and the point the callout names. */
const TOOLTIP_GAP = 10;

interface ChartTooltipProps {
  active?: boolean;
  label?: ReactNode;
  value?: ReactNode;
  /** Chart-space position of the hovered point, handed over by recharts. */
  coordinate?: { x?: number; y?: number };
  /** Plot area, used to keep the box inside it. */
  viewBox?: { x?: number; width?: number };
}

/**
 * Dark callout naming the hovered point, anchored to it by its arrow. It sits
 * to the right and flips to the left — arrow and all — only when the box would
 * not fit there, so it stays beside the cursor everywhere else. Deciding that
 * needs the rendered width, hence the measurement: recharts places the box off
 * its own wrapper and cannot be asked where it landed.
 *
 * Charts pass `allowEscapeViewBox={{ x: true }}` alongside this so recharts does
 * not flip the box a second time near the plot edge, which would leave it a
 * whole box-width away from what it points at.
 */
export const ChartTooltip = ({
  active,
  label,
  value,
  coordinate,
  viewBox,
}: ChartTooltipProps) => {
  const theme = useTheme();
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxWidth, setBoxWidth] = useState(0);

  // Re-measured on every move: the box is as wide as its label, so a stale
  // width would flip the box at the wrong moment once the label changes.
  useLayoutEffect(() => {
    const measured = boxRef.current?.offsetWidth ?? 0;
    if (measured && measured !== boxWidth) setBoxWidth(measured);
  });

  if (!active || value === undefined || value === null) return null;

  const plotRight = (viewBox?.x ?? 0) + (viewBox?.width ?? 0);
  const isFlipped =
    boxWidth > 0 && (coordinate?.x ?? 0) + TOOLTIP_GAP + boxWidth > plotRight;
  const background = theme.palette.lightBg.high;

  return (
    <Stack
      direction={"row"}
      ref={boxRef}
      sx={{
        position: "relative",
        backgroundColor: background,
        padding: "4px 8px",
        borderRadius: "4px",
        boxShadow: "0px 4px 8px 0px #0000001A",
        alignItems: "center",
        gap: "4px",
        whiteSpace: "nowrap",
        // Recharts has already placed the wrapper a gap to the right of the
        // point, so flipping means crossing back over it by that gap twice.
        transform: isFlipped
          ? `translate(calc(-100% - ${TOOLTIP_GAP * 2}px), -50%)`
          : "translate(0, -50%)",
        "&::after": {
          content: '""',
          position: "absolute",
          top: "50%",
          marginTop: "-6px",
          borderTop: "6px solid transparent",
          borderBottom: "6px solid transparent",
          ...(isFlipped
            ? { left: "100%", borderLeft: `8px solid ${background}` }
            : { right: "100%", borderRight: `8px solid ${background}` }),
        },
      }}
    >
      <Typography
        variant="body-xsmall"
        sx={{
          color: theme.palette.darkBg.high,
          textTransform: "capitalize",
        }}
      >
        {`${label}:`}
      </Typography>
      <Typography
        variant="body-xsmall"
        sx={{ color: theme.palette.darkBg.high }}
      >
        {value}
      </Typography>
    </Stack>
  );
};

interface CustomTooltipProps
  extends Omit<ChartTooltipProps, "label" | "value"> {
  payload?: Array<{ payload?: Record<string, ReactNode> }>;
  /** Keys into the hovered row for its name and its number. */
  labelKey?: string;
  valueKey?: string;
}

/** Names the hovered row of a single-series chart. */
export const CustomTooltip = ({
  active,
  payload,
  labelKey = "",
  valueKey = "",
  coordinate,
  viewBox,
}: CustomTooltipProps) => {
  const row = payload?.[0]?.payload;

  return (
    <ChartTooltip
      active={active && !!row}
      label={row?.[labelKey]}
      value={row?.[valueKey]}
      coordinate={coordinate}
      viewBox={viewBox}
    />
  );
};
